import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share, PlusSquare, X, MonitorSmartphone } from 'lucide-react';

let activeDeferredPrompt = null;
let installPromptAvailable = false;
const installPromptListeners = new Set();

const isStandaloneMode = () => (
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone ||
  document.referrer.includes('android-app://')
);

const setInstallPromptAvailable = (available) => {
  installPromptAvailable = available;
  installPromptListeners.forEach((listener) => listener(installPromptAvailable));
};

export const requestInstallPrompt = async () => {
  if (!activeDeferredPrompt || isStandaloneMode()) {
    setInstallPromptAvailable(false);
    return null;
  }

  activeDeferredPrompt.prompt();
  const { outcome } = await activeDeferredPrompt.userChoice;
  activeDeferredPrompt = null;
  setInstallPromptAvailable(false);
  return outcome;
};

export const useInstallPromptAvailability = () => {
  const [canInstall, setCanInstall] = useState(installPromptAvailable);

  useEffect(() => {
    setCanInstall(installPromptAvailable);
    installPromptListeners.add(setCanInstall);
    return () => installPromptListeners.delete(setCanInstall);
  }, []);

  return canInstall;
};

const InstallPrompt = ({ deferredPrompt }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isStandaloneApp = isStandaloneMode();
    activeDeferredPrompt = deferredPrompt || null;
    setInstallPromptAvailable(Boolean(deferredPrompt) && !isStandaloneApp);
    setIsStandalone(isStandaloneApp);

    if (isStandaloneApp) return;

    // Detect iOS
    const isIOSDevice = [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ].includes(navigator.platform)
    // iPad on iOS 13 detection
    || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
    
    setIsIOS(isIOSDevice);

    // If it's Android/Desktop and there's no deferredPrompt yet, we might want to wait.
    // But we should show it after 10 seconds regardless (iOS doesn't have deferredPrompt).
    const timer = setTimeout(() => {
      // Check if user previously dismissed it
      const hasDismissed = localStorage.getItem('installPromptDismissed');
      if (!hasDismissed && !isStandaloneApp) {
        setShowPrompt(true);
      }
    }, 10000); // 10 seconds

    return () => clearTimeout(timer);
  }, [deferredPrompt]);

  useEffect(() => {
    const handleAppInstalled = () => {
      activeDeferredPrompt = null;
      setInstallPromptAvailable(false);
      setIsStandalone(true);
      setShowPrompt(false);
    };

    window.addEventListener('appinstalled', handleAppInstalled);
    return () => window.removeEventListener('appinstalled', handleAppInstalled);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show the install prompt
      const outcome = await requestInstallPrompt();
      // Wait for the user to respond to the prompt
      console.log(`User response to the install prompt: ${outcome}`);
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
    } else if (isIOS) {
      // On iOS, we just show the instructions. We don't close unless they click X.
      // But we can add it to dismissed.
    }
  };

  const handleClose = () => {
    setShowPrompt(false);
    localStorage.setItem('installPromptDismissed', 'true');
  };

  if (!showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white rounded-2xl shadow-2xl z-50 overflow-hidden border border-gray-100"
      >
        <div className="p-4">
          <button 
            onClick={handleClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <MonitorSmartphone className="w-6 h-6 text-primary" />
            </div>
            
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">
                Instalo Aplikacionin
              </h3>
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                Shto aplikacionin e Xhamisë në ekranin tënd për qasje të shpejtë dhe për të marrë njoftime.
              </p>
              
              {isIOS ? (
                <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-700">
                  <p className="flex items-center gap-2 mb-2 font-medium">
                    Për të instaluar në iOS:
                  </p>
                  <ol className="list-decimal list-inside space-y-1.5 ml-1">
                    <li className="flex items-center gap-2">
                      Kliko <Share className="w-4 h-4 text-blue-500 inline" /> (Share) në fund të ekranit
                    </li>
                    <li className="flex items-center gap-2">
                      Zgjidh <PlusSquare className="w-4 h-4 text-gray-600 inline" /> <strong>Add to Home Screen</strong>
                    </li>
                  </ol>
                </div>
              ) : (
                <button
                  onClick={handleInstallClick}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-xl transition-colors shadow-lg shadow-primary/20"
                >
                  Instalo Tani
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InstallPrompt;
